(() => {
  if (window.FF_CART_PROGRESS_INITIALIZED) return;
  window.FF_CART_PROGRESS_INITIALIZED = true;

  const BASE_CURRENCY = 'EUR';
  const WIDGET_SELECTOR = '[data-ff-cart-progress]';

  const toNumber = (value) => {
    const normalized = String(value ?? '').trim().replace(',', '.');
    const number = Number(normalized);
    return Number.isFinite(number) ? number : 0;
  };

  const toBool = (value) => value === true || value === 'true';

  const getShopifyRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const fetchCart = async () => {
    try {
      const response = await fetch(`${getShopifyRoot()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    }
  };

  const getActiveCurrency = (widget, cart) => {
    return (
      cart?.currency ||
      window.Shopify?.currency?.active ||
      widget.dataset.currentCurrency ||
      BASE_CURRENCY
    ).toUpperCase();
  };

  const getConversionRate = (currency) => {
    if (!currency || currency === BASE_CURRENCY) return 1;

    const shopifyRate = Number(window.Shopify?.currency?.rate);
    if (Number.isFinite(shopifyRate) && shopifyRate > 0) {
      return shopifyRate;
    }

    if (window.Currency && typeof window.Currency.convert === 'function') {
      const converted = Number(window.Currency.convert(1, BASE_CURRENCY, currency));
      if (Number.isFinite(converted) && converted > 0) {
        return converted;
      }
    }

    return null;
  };

  const formatMoney = (cents, currency) => {
  const locale = document.documentElement.lang || undefined;
  const amount = cents / 100;

  if (!Number.isFinite(amount)) return '';

  const isWholeAmount = Math.abs(amount - Math.round(amount)) < 0.001;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: isWholeAmount ? 0 : 2,
      maximumFractionDigits: isWholeAmount ? 0 : 2
    }).format(amount);
  } catch (_error) {
    return isWholeAmount
      ? `${Math.round(amount)} ${currency}`
      : `${amount.toFixed(2)} ${currency}`;
  }
};

  const setText = (element, text) => {
    if (!element) return;
    element.textContent = String(text || '').trim();
  };

  const fillTemplate = (template, amount) => String(template || '').replace('__AMOUNT__', amount);

  const setHidden = (element, hidden) => {
    if (!element) return;
    element.hidden = Boolean(hidden);
  };

  const setMarkerComplete = (element, complete) => {
    if (!element) return;
    element.classList.toggle('is-complete', Boolean(complete));
  };

  const setCheckoutState = (widget, allowed) => {
    const form = widget.closest('form') || document;
    const buttons = form.querySelectorAll('button[name="checkout"], input[type="submit"][name="checkout"]');
    const wallets = form.querySelectorAll('.additional-checkout-buttons');

    buttons.forEach((button) => {
      if (!allowed) {
        if (button.dataset.ffCheckoutDisabled !== 'true') {
          button.dataset.ffWasDisabled = button.disabled ? 'true' : 'false';
        }

        button.dataset.ffCheckoutDisabled = 'true';
        button.classList.add('ff-checkout-is-disabled');
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
      } else if (button.dataset.ffCheckoutDisabled === 'true') {
        button.classList.remove('ff-checkout-is-disabled');
        button.removeAttribute('aria-disabled');

        if (button.dataset.ffWasDisabled !== 'true') {
          button.disabled = false;
        }

        delete button.dataset.ffCheckoutDisabled;
        delete button.dataset.ffWasDisabled;
      }
    });

    wallets.forEach((walletBlock) => {
      if (!allowed) {
        walletBlock.dataset.ffCheckoutDisabled = 'true';
        walletBlock.classList.add('ff-checkout-wallets-disabled');
        walletBlock.hidden = true;
      } else if (walletBlock.dataset.ffCheckoutDisabled === 'true') {
        walletBlock.classList.remove('ff-checkout-wallets-disabled');
        walletBlock.hidden = false;
        delete walletBlock.dataset.ffCheckoutDisabled;
      }
    });
  };

  const updateWidget = (widget, cart) => {
    const currency = getActiveCurrency(widget, cart);
    const rate = getConversionRate(currency);
    const cartTotal = Number(cart?.total_price ?? widget.dataset.cartTotal ?? 0);

    const showMov = toBool(widget.dataset.showMov);
    const showFreeShipping = toBool(widget.dataset.showFreeShipping);
    const disableCheckout = toBool(widget.dataset.disableCheckoutBelowMov);
    const freeShippingAvailable = toBool(widget.dataset.freeShippingAvailable);

    const movEur = toNumber(widget.dataset.movEur);
    const freeShippingEur = toNumber(widget.dataset.freeShippingEur);

    const movLine = widget.querySelector('[data-ff-progress-mov]');
    const shippingLine = widget.querySelector('[data-ff-progress-shipping]');
    const helperLine = widget.querySelector('[data-ff-progress-text]');
    const meter = widget.querySelector('[data-ff-progress-meter]');
    const fill = widget.querySelector('[data-ff-progress-fill]');
    const movMarker = widget.querySelector('[data-ff-progress-marker-mov]');
    const shippingMarker = widget.querySelector('[data-ff-progress-marker-shipping]');
    const movStep = widget.querySelector('[data-ff-progress-step-mov]');
    const shippingStep = widget.querySelector('[data-ff-progress-step-shipping]');

    if (!rate) {
      setCheckoutState(widget, true);
      return;
    }

    const movThreshold = Math.round(movEur * rate * 100);
    const shippingThreshold = Math.round(freeShippingEur * rate * 100);
    const movRemaining = Math.max(0, movThreshold - cartTotal);
    const shippingRemaining = Math.max(0, shippingThreshold - cartTotal);

    const movAmount = formatMoney(movThreshold, currency);
    const shippingAmount = formatMoney(shippingThreshold, currency);
    const movRemainingAmount = formatMoney(movRemaining, currency);
    const shippingRemainingAmount = formatMoney(shippingRemaining, currency);

    const movReached = movThreshold > 0 && cartTotal >= movThreshold;
    const shippingReached = shippingThreshold > 0 && cartTotal >= shippingThreshold;
    const canShowMeter = freeShippingAvailable && showFreeShipping && shippingThreshold > 0;

    const progressPercent = canShowMeter
      ? Math.min(100, Math.max(0, (cartTotal / shippingThreshold) * 100))
      : 0;

    const movMarkerPercent = canShowMeter && movThreshold > 0
      ? Math.min(100, Math.max(0, (movThreshold / shippingThreshold) * 100))
      : 0;

    const showMovMarker = canShowMeter && showMov && movThreshold > 0 && movMarkerPercent > 0 && movMarkerPercent < 100;

    if (!freeShippingAvailable) {
      if (showMov && movThreshold > 0) {
        setText(movLine, fillTemplate(widget.dataset.movTitleTemplate, movAmount));
        setHidden(movLine, false);
      } else {
        setHidden(movLine, true);
      }

      setHidden(shippingLine, true);
      setHidden(helperLine, true);
      setHidden(meter, true);
    } else if (showMov && movThreshold > 0 && cartTotal < movThreshold) {
      setText(movLine, fillTemplate(widget.dataset.movTitleTemplate, movAmount));
      setText(helperLine, fillTemplate(widget.dataset.movRemainingTemplate, movRemainingAmount));
      setHidden(movLine, false);
      setHidden(shippingLine, true);
      setHidden(helperLine, false);
    } else if (showFreeShipping && shippingThreshold > 0 && cartTotal < shippingThreshold) {
      setText(shippingLine, fillTemplate(widget.dataset.shippingTitleTemplate, shippingAmount));
      setText(helperLine, fillTemplate(widget.dataset.shippingRemainingTemplate, shippingRemainingAmount));
      setHidden(movLine, true);
      setHidden(shippingLine, false);
      setHidden(helperLine, false);
    } else if (showFreeShipping && shippingThreshold > 0) {
      setText(shippingLine, widget.dataset.shippingReachedTitle || '');
      setText(helperLine, widget.dataset.shippingReachedText || '');
      setHidden(movLine, true);
      setHidden(shippingLine, false);
      setHidden(helperLine, false);
    } else {
      if (showMov && movThreshold > 0) {
        setText(movLine, fillTemplate(widget.dataset.movTitleTemplate, movAmount));
        setHidden(movLine, false);
      } else {
        setHidden(movLine, true);
      }

      setHidden(shippingLine, true);
      setHidden(helperLine, true);
    }

    if (canShowMeter) {
      widget.style.setProperty('--ff-progress', `${progressPercent}%`);
      widget.style.setProperty('--ff-mov-marker', `${movMarkerPercent}%`);
      if (meter) {
        meter.style.setProperty('--ff-progress', `${progressPercent}%`);
        meter.style.setProperty('--ff-mov-marker', `${movMarkerPercent}%`);
      }
      if (fill) fill.style.width = `${progressPercent}%`;
      setMarkerComplete(movMarker, movReached);
      setMarkerComplete(shippingMarker, shippingReached);
      setText(movStep, movAmount);
      setText(shippingStep, shippingAmount);
      setHidden(movMarker, !showMovMarker);
      setHidden(movStep, !showMovMarker);
      setHidden(meter, false);
    } else {
      setHidden(meter, true);
    }

    const checkoutAllowed = !(disableCheckout && movThreshold > 0 && cartTotal < movThreshold);
    setCheckoutState(widget, checkoutAllowed);
  };

  let scheduled = false;
  let updateToken = 0;

  const updateAll = async () => {
    scheduled = false;
    const token = ++updateToken;

    const widgets = Array.from(document.querySelectorAll(WIDGET_SELECTOR));
    if (!widgets.length) return;

    const cart = await fetchCart();
    if (token !== updateToken) return;

    widgets.forEach((widget) => updateWidget(widget, cart));
  };

  const scheduleUpdate = () => {
    if (scheduled) return;
    scheduled = true;
    window.setTimeout(updateAll, 100);
  };

  document.addEventListener('DOMContentLoaded', scheduleUpdate);
  document.addEventListener('cart:change', scheduleUpdate);
  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);
  document.addEventListener('variant:change', scheduleUpdate);

  document.addEventListener('click', (event) => {
    if (
      event.target.closest('quantity-selector') ||
      event.target.closest('line-item-quantity') ||
      event.target.closest('[name="updates[]"]') ||
      event.target.closest('.quantity-selector__button') ||
      event.target.closest('[aria-label*="Remove"]')
    ) {
      scheduleUpdate();
    }
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches?.(WIDGET_SELECTOR) || node.querySelector?.(WIDGET_SELECTOR)) {
          scheduleUpdate();
          return;
        }
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  scheduleUpdate();
})();
